import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { storeDamagedStockAPI, posAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, AlertTriangle, Package, Search, Filter,
  Edit, Trash2, Clock, CheckCircle, XCircle, Ban
} from 'lucide-react';

const StoreDamagedStock = () => {
  const { user } = useAuth();
  const storeId = user?.entity_id;

  const [entries, setEntries] = useState([]);
  const [damageTypes, setDamageTypes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);

  // Product search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    quantity: '',
    damage_type: '',
    damage_date: new Date().toISOString().split('T')[0],
    reason: '',
    notes: ''
  });

  useEffect(() => {
    if (storeId) {
      loadData();
    }
  }, [storeId, filterStatus, filterType]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [typesRes, entriesRes, summaryRes] = await Promise.all([
        storeDamagedStockAPI.getDamageTypes(),
        storeDamagedStockAPI.list({
          store_id: storeId,
          status: filterStatus !== 'all' ? filterStatus : undefined,
          damage_type: filterType !== 'all' ? filterType : undefined
        }),
        storeDamagedStockAPI.getSummary(storeId)
      ]);
      
      setDamageTypes(typesRes.data);
      setEntries(entriesRes.data.items || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load damaged stock records');
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      setSearching(true);
      const response = await posAPI.searchProducts(query, storeId);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchProducts(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleOpenModal = () => {
    setSelectedProduct(null);
    setSearchQuery('');
    setSearchResults([]);
    setFormData({
      quantity: '',
      damage_type: '',
      damage_date: new Date().toISOString().split('T')[0],
      reason: '',
      notes: ''
    });
    setModalOpen(true);
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }
    
    if (!formData.quantity || !formData.damage_type || !formData.reason) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const payload = {
        store_id: storeId,
        product_id: selectedProduct.product_id,
        product_name: selectedProduct.product_name,
        product_sku: selectedProduct.sku,
        quantity: parseFloat(formData.quantity),
        damage_type: formData.damage_type,
        damage_date: formData.damage_date,
        reason: formData.reason,
        notes: formData.notes
      };

      await storeDamagedStockAPI.create(payload);
      toast.success('Damaged stock entry created successfully');
      setModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to create entry');
    }
  };

  const handleDelete = async () => {
    if (!entryToDelete) return;
    
    try {
      await storeDamagedStockAPI.delete(entryToDelete.id);
      toast.success('Entry deleted and stock restored');
      setDeleteConfirmOpen(false);
      setEntryToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const getDamageTypeName = (typeId) => {
    const type = damageTypes.find(t => t.id === typeId);
    return type?.name || typeId;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', variant: 'outline', icon: Clock, color: 'text-yellow-600' },
      approved: { label: 'Approved', variant: 'default', icon: CheckCircle, color: 'text-green-600' },
      rejected: { label: 'Rejected', variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      written_off: { label: 'Written Off', variant: 'secondary', icon: Ban, color: 'text-gray-600' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
        <Icon className={`w-3 h-3 ${config.color}`} />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="p-6 space-y-6" data-testid="store-damaged-stock-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Damaged Stock</h1>
          <p className="text-gray-500">Record and track damaged or expired inventory</p>
        </div>
        <Button onClick={handleOpenModal} data-testid="add-damaged-stock-btn">
          <Plus className="w-4 h-4 mr-2" />
          Report Damaged Stock
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Entries</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.total_entries || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Quantity</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.total_quantity || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Estimated Loss</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(summary.total_value || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Approval</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {summary.by_status?.pending?.count || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="min-w-[180px]">
              <Label>Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="written_off">Written Off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <Label>Damage Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-damage-type">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {damageTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              variant="outline" 
              onClick={() => {
                setFilterStatus('all');
                setFilterType('all');
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle>Damaged Stock Records</CardTitle>
          <CardDescription>
            {entries.length} record{entries.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading records...</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No damaged stock records found.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entry #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Est. Loss</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id} data-testid={`damaged-stock-row-${entry.id}`}>
                    <TableCell className="font-mono text-sm">
                      {entry.entry_number}
                    </TableCell>
                    <TableCell>
                      {new Date(entry.damage_date).toLocaleDateString('en-IN')}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{entry.product_name}</p>
                        <p className="text-xs text-gray-500">{entry.product_sku}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{getDamageTypeName(entry.damage_type)}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {entry.quantity}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-red-600">
                      {formatCurrency(entry.estimated_loss || 0)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(entry.status)}
                    </TableCell>
                    <TableCell>
                      {entry.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => {
                            setEntryToDelete(entry);
                            setDeleteConfirmOpen(true);
                          }}
                          data-testid={`delete-entry-${entry.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Damaged Stock Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Report Damaged Stock</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Product Search */}
            <div>
              <Label>Product *</Label>
              {selectedProduct ? (
                <div className="border rounded-lg p-3 bg-gray-50 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{selectedProduct.product_name}</p>
                    <p className="text-sm text-gray-500">
                      SKU: {selectedProduct.sku} | Available: {selectedProduct.quantity}
                    </p>
                  </div>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSelectedProduct(null)}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by product name, SKU, or barcode..."
                    className="pl-10"
                    data-testid="product-search-input"
                  />
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {searchResults.map((product) => (
                        <div
                          key={product.product_id}
                          className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => handleSelectProduct(product)}
                        >
                          <p className="font-medium">{product.product_name}</p>
                          <p className="text-sm text-gray-500">
                            SKU: {product.sku} | Stock: {product.quantity}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {searching && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-center text-gray-500">
                      Searching...
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="0"
                  min="0.01"
                  step="0.01"
                  max={selectedProduct?.quantity || 9999}
                  data-testid="quantity-input"
                />
                {selectedProduct && (
                  <p className="text-xs text-gray-500 mt-1">
                    Max: {selectedProduct.quantity}
                  </p>
                )}
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.damage_date}
                  onChange={(e) => setFormData({ ...formData, damage_date: e.target.value })}
                  data-testid="damage-date-input"
                />
              </div>
            </div>

            <div>
              <Label>Damage Type *</Label>
              <Select
                value={formData.damage_type}
                onValueChange={(value) => setFormData({ ...formData, damage_type: value })}
              >
                <SelectTrigger data-testid="damage-type-select">
                  <SelectValue placeholder="Select damage type" />
                </SelectTrigger>
                <SelectContent>
                  {damageTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      <div>
                        <span>{type.name}</span>
                        <span className="text-xs text-gray-500 ml-2">- {type.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Reason *</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Describe how the damage occurred..."
                rows={2}
                data-testid="reason-input"
              />
            </div>

            <div>
              <Label>Additional Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional information..."
                rows={2}
                data-testid="notes-input"
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-yellow-800">Important:</p>
              <p className="text-yellow-700">
                Submitting this form will immediately deduct the specified quantity from store inventory. 
                The entry will be sent to HQ for approval.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-damaged-stock-btn">
                Submit Entry
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Entry</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Are you sure you want to delete this entry? The damaged quantity will be restored to inventory.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete & Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreDamagedStock;
